<template>
    <div class="tw-flex tw-min-w-0 tw-items-center tw-select-none">
        <div
            v-if="user && !hideAvatar"
            class="tw-relative tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-cover tw-bg-center tw-font-semibold tw-leading-none tw-shadow-sm"
            :class="avatarSizeClasses"
            :style="avatarStyles"
            :title="hideName ? displayName : undefined"
        >
            <span v-if="!user?.avatar_url" class="tw-select-none tw-uppercase">
                {{ userInitials }}
            </span>
        </div>
        <div
            v-if="!hideName"
            class="tw-min-w-0 tw-truncate tw-text-sm tw-leading-5 tw-text-current"
            :class="{
                'tw-ml-2': !hideAvatar,
            }"
        >
            <slot :user="user">{{ displayName }}</slot>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { CSSProperties, PropType } from 'vue';
import { computed } from 'vue';
import type { User } from '@/js/types/user.ts';
import { contrastingTextColour, hashStringToColour, initials } from '@/js/utils.js';

type UserAvatarSize = 'xsmall' | 'small' | 'large' | 'xlarge' | 'xxlarge';

const props = defineProps({
    user: {
        type: Object as PropType<User | null>,
        default: null,
    },
    hideAvatar: {
        type: Boolean,
        default: false,
    },
    hideName: {
        type: Boolean,
        default: true,
    },
    size: {
        type: String as PropType<UserAvatarSize | null>,
        default: null,
    },
});

const avatarSizeClasses = computed(() => {
    switch (props.size) {
        case 'xsmall':
            return 'tw-h-7 tw-w-7 tw-text-[11px]';
        case 'small':
            return 'tw-h-8 tw-w-8 tw-text-xs';
        case 'large':
            return 'tw-h-[42px] tw-w-[42px] tw-text-base';
        case 'xlarge':
            return 'tw-h-12 tw-w-12 tw-text-lg';
        case 'xxlarge':
            return 'tw-h-16 tw-w-16 tw-text-xl';
        default:
            return 'tw-h-9 tw-w-9 tw-text-sm';
    }
});

const displayName = computed(() => props.user?.name || props.user?.email || 'Unknown user');

const userInitials = computed(() => initials(displayName.value)?.slice(0, 2) ?? '');

const avatarStyles = computed<CSSProperties>(() => {
    if (props.user?.avatar_url) {
        return {
            backgroundImage: `url(${props.user.avatar_url})`,
        };
    }

    const backgroundColor = hashStringToColour(displayName.value);

    return {
        backgroundColor,
        color: contrastingTextColour(backgroundColor, '#FFFFFF', '#303e67'),
    };
});
</script>
